import { AUTH_FALLBACK_REDIRECT_URL, resolveAuthFallbackRedirectUrl } from "@/lib/auth-redirect";

/**
 * Browser half of iOS Sign in with Apple. The server trades Apple's identity
 * token for a one-time Clerk ticket (see `native-apple-session.ts`); the page
 * only redeems that ticket, because Clerk runs no captcha on a ticket sign-in.
 */

const SESSION_EXISTS_CODE = "session_exists";
const APPLE_NAME_KEY_PREFIX = "ap:apple-name:";

/** Any top-frame navigation to Apple's sign-in host makes the iOS app open its native sheet. */
export const NATIVE_APPLE_SHEET_TRIGGER_URL = "https://appleid.apple.com/auth/authorize";

const CLERK_APPLE_BUTTON_SELECTOR =
  ".cl-socialButtonsBlockButton__apple, .cl-socialButtonsIconButton__apple";

export interface TicketSignInAttempt {
  status: string | null;
  createdSessionId: string | null;
}

export type TicketSignIn = (params: {
  strategy: "ticket";
  ticket: string;
}) => Promise<TicketSignInAttempt>;

export type AppleTicketRedemption =
  | { kind: "complete"; sessionId: string }
  | { kind: "already-signed-in" }
  | { kind: "failed"; code: string };

export interface AppleNameCredential {
  identityToken: string;
  firstName: string | null;
  lastName: string | null;
}

type NameStorage = Pick<Storage, "getItem" | "setItem">;

export function clerkErrorCode(error: unknown): string {
  if (error && typeof error === "object" && "errors" in error) {
    const errors = (error as { errors?: unknown }).errors;
    if (Array.isArray(errors)) {
      const code: unknown = (errors[0] as { code?: unknown } | undefined)?.code;
      if (typeof code === "string" && code.length > 0) {
        return code;
      }
    }
  }
  return "unknown";
}

export async function redeemAppleSignInTicket(
  signIn: TicketSignIn,
  ticket: string,
): Promise<AppleTicketRedemption> {
  let attempt: TicketSignInAttempt;
  try {
    attempt = await signIn({ strategy: "ticket", ticket });
  } catch (error) {
    const code = clerkErrorCode(error);
    return code === SESSION_EXISTS_CODE ? { kind: "already-signed-in" } : { kind: "failed", code };
  }
  if (attempt.status === "complete" && attempt.createdSessionId) {
    return { kind: "complete", sessionId: attempt.createdSessionId };
  }
  return { kind: "failed", code: `ticket_${attempt.status ?? "incomplete"}` };
}

function appleIdentityClaims(identityToken: string): Record<string, unknown> | null {
  try {
    const payload = identityToken.split(".")[1] ?? "";
    const claims: unknown = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return claims && typeof claims === "object" ? (claims as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function appleIdentitySubject(identityToken: string): string | null {
  const sub = appleIdentityClaims(identityToken)?.sub;
  return typeof sub === "string" && sub.length > 0 ? sub : null;
}

/** Apple sends these booleans as either JSON booleans or the strings "true"/"false". */
function appleBoolean(value: unknown): boolean | null {
  if (typeof value === "boolean") {
    return value;
  }
  return value === "true" ? true : value === "false" ? false : null;
}

export type AppleIdentityTokenDiagnostics = Record<string, string | number | boolean | null>;

/**
 * What Apple put in the identity token, for analytics. Never includes the
 * email address or the Apple user id.
 */
export function appleIdentityTokenDiagnostics(identityToken: string): AppleIdentityTokenDiagnostics {
  const claims = appleIdentityClaims(identityToken);
  if (!claims) {
    return { token_readable: false };
  }
  const email = claims.email;
  return {
    token_readable: true,
    token_aud: typeof claims.aud === "string" ? claims.aud : null,
    token_has_email: typeof email === "string" && email.length > 0,
    token_email_verified: appleBoolean(claims.email_verified),
    token_is_private_email: appleBoolean(claims.is_private_email),
    token_real_user_status:
      typeof claims.real_user_status === "number" ? claims.real_user_status : null,
    token_has_nonce: typeof claims.nonce === "string" && claims.nonce.length > 0,
  };
}

function readStoredName(
  storage: NameStorage,
  key: string,
): Pick<AppleNameCredential, "firstName" | "lastName"> | null {
  try {
    const stored = JSON.parse(storage.getItem(key) ?? "null") as Record<string, unknown> | null;
    if (!stored || typeof stored !== "object") {
      return null;
    }
    return {
      firstName: typeof stored.firstName === "string" ? stored.firstName : null,
      lastName: typeof stored.lastName === "string" ? stored.lastName : null,
    };
  } catch {
    return null;
  }
}

/**
 * Apple shares the user's name only on the first authorization. If that
 * attempt fails, the retry has no name and Clerk cannot create the account,
 * so the name is kept per Apple user until it is used.
 */
export function withRememberedAppleName<T extends AppleNameCredential>(
  credential: T,
  storage: NameStorage | null,
): T {
  const subject = appleIdentitySubject(credential.identityToken);
  if (!storage || !subject) {
    return credential;
  }
  const key = `${APPLE_NAME_KEY_PREFIX}${subject}`;
  try {
    if (credential.firstName || credential.lastName) {
      storage.setItem(
        key,
        JSON.stringify({ firstName: credential.firstName, lastName: credential.lastName }),
      );
      return credential;
    }
  } catch {
    return credential;
  }
  const stored = readStoredName(storage, key);
  return stored ? { ...credential, ...stored } : credential;
}

export function findClerkAppleButton(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) {
    return null;
  }
  const button = target.closest<HTMLElement>(CLERK_APPLE_BUTTON_SELECTOR);
  if (!button || button.hasAttribute("disabled") || button.getAttribute("aria-disabled") === "true") {
    return null;
  }
  return button;
}

/**
 * Where the sign-in page was headed. Clerk's own redirects pass an absolute
 * same-origin `redirect_url`; app links pass a path.
 */
export function nativeAppleRedirectPath(search: string, origin: string): string {
  const target = new URLSearchParams(search).get("redirect_url")?.trim();
  if (!target) {
    return AUTH_FALLBACK_REDIRECT_URL;
  }
  try {
    const url = new URL(target, origin);
    if (url.origin !== origin) {
      return AUTH_FALLBACK_REDIRECT_URL;
    }
    return resolveAuthFallbackRedirectUrl(`${url.pathname}${url.search}${url.hash}`);
  } catch {
    return AUTH_FALLBACK_REDIRECT_URL;
  }
}

export type AppleSignUpErrorKey =
  | "emailTaken"
  | "emailInvalid"
  | "codeIncorrect"
  | "codeExpired"
  | "tooManyAttempts"
  | "expired"
  | "generic";

const APPLE_SIGN_UP_ERROR_KEYS: Record<string, AppleSignUpErrorKey> = {
  form_identifier_exists: "emailTaken",
  form_param_format_invalid: "emailInvalid",
  form_code_incorrect: "codeIncorrect",
  verification_expired: "codeExpired",
  verification_failed: "tooManyAttempts",
  too_many_requests: "tooManyAttempts",
  pending_expired: "expired",
};

/** Message key under `Auth.appleSignUpErrors` for a failed sign-up step. */
export function appleSignUpErrorKey(code: string): AppleSignUpErrorKey {
  return APPLE_SIGN_UP_ERROR_KEYS[code] ?? "generic";
}
